import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { IonSelect } from "@ionic/angular";
import { Store } from "@ngrx/store";
import * as fromApp from "../../../../../store/app.reducer";
import * as CartCustomOptionsSelectionsActions from "../../store/cart-custom-options-selections/cart-custom-options-selections.actions";

import * as _ from "lodash";
import { auditTime, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Subscription } from "rxjs";
import moment from "moment-timezone";
import { normalizeCartCustomOptionSelection } from "../../cart-custom-options.helpers";

// Popup variant of a radio (single-choice) cart custom option: same NgRx
// selection flow as app-radio-type-choices, but the choices are presented in an
// ion-select popup instead of an inline list (option.showAsPopup === true).
// Note: the per-choice min-order hint that the inline radio renders is not shown
// here; min-order validation still runs in the parent complete-order check.
@Component({
  selector: "app-select-type-choices",
  templateUrl: "./select-type-choices.component.html",
  styleUrls: ["./select-type-choices.component.scss"],
})
export class SelectTypeChoicesComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() option_timestamp;
  @Input() selectedLang: any;
  @ViewChild("choiceSelect") ionSelectRef: IonSelect;
  public option: any;
  public CustomSelections;
  public finalPriceLoading;
  public selectedChoiceTimestamp;
  public cartPrices;
  public grand_total;
  // Header shown at the top of the ion-select alert (option name, or the first
  // choice label when the option has no title).
  public interfaceOptions: any = {};

  private subscriptions: Subscription[] = [];
  private doNotCheckAllChoicesPeriod = false;
  private notValidCustomOptions: any[] = [];
  private completeOrderValidations: any[] = [];
  private pendingAutoOpenValidTs = null;
  private completeOrderLastTs = null;

  constructor(private store: Store<fromApp.AppState>) {}

  ngOnChanges(changes: SimpleChanges) {
    // Radio renders its labels live in the template; here they are precomputed,
    // so rebuild them when the language changes to keep parity with radio.
    if (changes["selectedLang"] && !changes["selectedLang"].firstChange) {
      this.buildChoiceDisplayLabels();
      this.buildOptionHeaderLabel();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store
        .select("cartCustomOptionsSelections")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.cartCustomOptionsSelections &&
            !_.isEqual(state.cartCustomOptionsSelections, this.CustomSelections)
          ) {
            this.CustomSelections = _.cloneDeep(
              state.cartCustomOptionsSelections
            );
            this.option = _.cloneDeep(
              _.find(this.CustomSelections, {
                timestamp: this.option_timestamp,
              })
            );

            if (
              this.option &&
              this.option.choices &&
              this.option.choices.length > 0
            ) {
              this.option.choices.forEach((choice) => {
                if (choice.selected) {
                  this.selectedChoiceTimestamp = choice.timestamp;
                }
              });
            }

            this.buildChoiceDisplayLabels();
            this.buildOptionHeaderLabel();

            if (this.doNotCheckAllChoicesPeriod) {
              this.doNotCheckAllChoicesPeriod = false;
              this.checkAllChoicesForShowPeriodDebounced();
            } else {
              this.checkAllChoicesForShowPeriodDebounced();
            }
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("disableFinalPrice")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(100))
        .subscribe((state) => {
          if (
            state &&
            !_.isEqual(state.disableFinalPrice, this.finalPriceLoading)
          ) {
            this.finalPriceLoading = _.cloneDeep(state.disableFinalPrice);
            // If an auto-open was armed while the price was still loading,
            // retry now that the select is enabled again.
            this.maybeAutoOpenPopup();
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("cartPrices")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(100))
        .subscribe((state) => {
          if (state && !_.isEqual(state.cartPrices, this.cartPrices)) {
            this.cartPrices = _.cloneDeep(state.cartPrices);

            if (this.cartPrices && this.cartPrices.grand_total) {
              this.grand_total = parseFloat(
                _.cloneDeep(this.cartPrices.grand_total)
              );
            } else {
              this.grand_total = 0;
            }
          }
        })
    );

    // Live list of required-but-unfilled options, produced by the parent
    // validator. Read-only: used only to pick which single popup to auto-open.
    this.subscriptions.push(
      this.store
        .select("cartCustomerOptions")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            !_.isEqual(state.notValidCustomOptions, this.notValidCustomOptions)
          ) {
            this.notValidCustomOptions = _.cloneDeep(
              state.notValidCustomOptions || []
            );
          }
        })
    );

    // "Complete order" was pressed: after the validators settle (same 300ms
    // debounce cart-list uses) arm a one-shot auto-open for this required popup.
    // maybeAutoOpenPopup then opens it ONLY if nothing else is blocking checkout,
    // so higher-priority errors (delivery method, address, ...) show first.
    // Gated on the timestamp so it never fires on load, only on a button press.
    this.subscriptions.push(
      this.store
        .select("completeOrderValidation")
        .pipe(debounceTime(300))
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.lastValidTimestamp &&
            !_.isEqual(state.lastValidTimestamp, this.completeOrderLastTs)
          ) {
            this.completeOrderLastTs = _.cloneDeep(state.lastValidTimestamp);
            this.completeOrderValidations = _.cloneDeep(
              state.completeOrderValidations || []
            );
            this.pendingAutoOpenValidTs = state.lastValidTimestamp;
            this.maybeAutoOpenPopup();
          }
        })
    );
  }

  // Precompute the visible label per choice (translated title + optional price)
  // so the template binds plain values instead of computing them in bindings.
  private buildChoiceDisplayLabels() {
    if (this.option && this.option.choices && this.option.choices.length > 0) {
      this.option.choices.forEach((choice) => {
        let title = choice.title;

        if (
          choice.translations &&
          choice.translations.title &&
          choice.translations.title[this.selectedLang]
        ) {
          title = choice.translations.title[this.selectedLang];
        }

        const priceSuffix = choice.price
          ? " - " + choice.price.toFixed(2) + "€"
          : "";

        choice.displayLabel = (title || "") + priceSuffix;
      });
    }
  }

  // Header shown at the top of the ion-select alert: the translated option
  // title, or (when the option has no title) the selected/first choice label.
  private buildOptionHeaderLabel() {
    let header = "";

    if (this.option) {
      header = this.option.title || "";

      if (
        this.option.translations &&
        this.option.translations.title &&
        this.option.translations.title[this.selectedLang]
      ) {
        header = this.option.translations.title[this.selectedLang];
      }

      if (!header && this.option.choices && this.option.choices.length > 0) {
        const selectedChoice = _.find(this.option.choices, { selected: true });
        const choice = selectedChoice || this.option.choices[0];
        header = (choice && (choice.displayLabel || choice.title)) || "";
      }
    }

    this.interfaceOptions = { header: header };
  }

  // When "complete order" is pressed with this required popup still empty, open
  // it automatically. Only the first not-valid popup option opens, so two
  // required popups never stack their alerts.
  private maybeAutoOpenPopup() {
    if (!this.pendingAutoOpenValidTs) {
      return;
    }
    // Time-box the armed press: a stale flag must never open the popup on a
    // later, unrelated notValidCustomOptions recalc.
    if (
      typeof this.pendingAutoOpenValidTs === "number" &&
      Date.now() - this.pendingAutoOpenValidTs > 10000
    ) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    if (!this.option || !this.option.isRequired) {
      return;
    }
    // Already filled -> nothing to prompt for.
    if (this.selectedChoiceTimestamp) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    // Only surface this popup when it is the LAST thing blocking checkout: if any
    // other validation (delivery method, address, invoice, ...) is still failing,
    // let that error show first. Mirrors cart-list's priority handler.
    const otherValidationFailing = _.some(
      this.completeOrderValidations,
      (v) => v && v.valid === false && v.id !== "cart-custom-options"
    );
    if (otherValidationFailing) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    const firstNotValidPopup = _.find(
      this.notValidCustomOptions,
      (o) => o && o.showAsPopup
    );
    // Validation may not have populated the list yet; wait for the
    // cartCustomerOptions subscription to fire again.
    if (!firstNotValidPopup) {
      return;
    }
    if (firstNotValidPopup.timestamp !== this.option_timestamp) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    // Select is disabled while the price loads; keep the flag armed and retry
    // from the disableFinalPrice subscription once it settles.
    if (this.finalPriceLoading) {
      return;
    }
    if (this.ionSelectRef) {
      this.pendingAutoOpenValidTs = null;
      this.ionSelectRef.open();
    }
  }

  checkForSelectedChoiceIgnoringPeriod(choice) {
    let ignoreShowPeriod = false;

    if (
      choice &&
      choice.ignoreShowPeriodWhenOptionObject &&
      choice.ignoreShowPeriodWhenOptionObject.option_timestamp &&
      this.CustomSelections
    ) {
      const option = _.find(this.CustomSelections, {
        timestamp: choice.ignoreShowPeriodWhenOptionObject.option_timestamp,
      });

      if (option && option.choices) {
        _.each(
          choice.ignoreShowPeriodWhenOptionObject.choices,
          (ingore_choice) => {
            const found = _.find(option.choices, {
              timestamp: ingore_choice.timestamp,
            });

            if (found && found.selected) {
              ignoreShowPeriod = true;
            }
          }
        );
      }
    }

    return ignoreShowPeriod;
  }

  private checkAllChoicesForShowPeriodDebounced = _.debounce(() => {
    this.checkAllChoicesForShowPeriod();
  }, 200);

  checkAllChoicesForShowPeriod() {
    if (this.option && this.option.choices && this.option.choices.length > 0) {
      this.option.choices.forEach((choice) => {
        if (
          choice.enableShowPeriod &&
          choice.start_timestamp &&
          choice.stop_timestamp &&
          !this.checkForSelectedChoiceIgnoringPeriod(choice)
        ) {
          choice.outOfPeriod = this.checkShowPeriod(_.cloneDeep(choice));

          if (choice.outOfPeriod) {
            choice.selected = false;

            if (choice.timestamp === this.selectedChoiceTimestamp) {
              this.selectedChoiceTimestamp = null;
            }
          }
        } else if (
          choice.enableShowPeriod &&
          choice.start_timestamp &&
          choice.stop_timestamp &&
          this.checkForSelectedChoiceIgnoringPeriod(choice)
        ) {
          delete choice.outOfPeriod;
        }
      });
    }
  }

  checkShowPeriod(ch) {
    const today = moment();

    const startDate = moment(ch.start_timestamp).set({
      year: today.year(),
      month: today.month(),
      date: today.date(),
    });

    const stopDate = moment(ch.stop_timestamp).set({
      year: today.year(),
      month: today.month(),
      date: today.date(),
    });

    if (today.isBefore(startDate) || today.isAfter(stopDate)) {
      //out of period
      return true;
    }

    return false;
  }

  onSelectionChange(ev) {
    this.checkAllChoicesForShowPeriod();

    if (this.selectedChoiceTimestamp) {
      let choiceIndex = _.findIndex(this.option.choices, {
        timestamp: this.selectedChoiceTimestamp,
      });
      this.option.choices.forEach((choice) => {
        choice.selected = false;
      });
      if (choiceIndex != -1) {
        this.option.choices[choiceIndex].selected = true;
        this.store.dispatch(
          new CartCustomOptionsSelectionsActions.AddUpdateCartCustomOptionSelections(
            normalizeCartCustomOptionSelection(this.option)
          )
        );
      }
    } else {
      this.option.choices.forEach((choice) => {
        choice.selected = false;
      });
      this.store.dispatch(
        new CartCustomOptionsSelectionsActions.AddUpdateCartCustomOptionSelections(
          normalizeCartCustomOptionSelection(this.option)
        )
      );
    }
  }

  trackTimestamp(index, choice) {
    return choice.timestamp;
  }

  ngOnDestroy() {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
